/**
 * Hotels Popups Slice
 *
 * Centralized popup state for hotel actions (similar to flights popupsSlice).
 * Handles popups triggered by intents and chat responses:
 * - QnA Gallery (HOTEL_MEDIA intent - show images from chat)
 * - API errors
 *
 * Structure:
 * - currentPopup: key of currently shown popup (null = no popup)
 * - popupMap: map of popup key to popup data
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  setActiveTemplate,
  updateTemplateFromStream,
} from '@/features/chat/redux/slices/templateSlice';
import {
  extractMediaFromQnATemplateData,
  extractIntentsFromHotelResponse,
  HOTEL_INTENTS,
} from '@/features/hotels/utils/intentHandler';
import { DATA_ONLY_TEMPLATE_TYPES } from '@/features/chat/constants/templateTypes';

/** QnA media category from agent response */
export interface QnAMediaCategory {
  /** Top-level tab: "Property" or "Traveller" */
  tab: string;
  category: string;
  images: string[];
  image_count: number;
}

// ============================================
// Popup Constants
// ============================================

/** Key for QnA gallery popup (HOTEL_MEDIA intent) */
export const QNA_GALLERY_POPUP_KEY = 'QNA_GALLERY';

/** Key for API error popup */
export const API_ERROR_POPUP_KEY = 'API_ERROR';

// ============================================
// Popup Types
// ============================================

/** QnA Gallery popup data */
export interface QnAGalleryPopupData {
  categories: QnAMediaCategory[];
}

/** Error popup data */
export interface PopupErrorDetails {
  errorMessage: string;
  errorDescription?: string;
}

/** Union of all popup data types */
export type HotelsPopupData = QnAGalleryPopupData | PopupErrorDetails;

export interface HotelsPopupsState {
  /** Currently active popup key (null = no popup shown) */
  currentPopup: string | null;
  /** Map of popup key to popup data */
  popupMap: Record<string, HotelsPopupData>;
}

// ============================================
// Initial State
// ============================================

const initialPopupsState: HotelsPopupsState = {
  currentPopup: null,
  popupMap: {},
};

// ============================================
// Slice
// ============================================

const popupsSlice = createSlice({
  name: 'hotels/popups',
  initialState: initialPopupsState,
  reducers: {
    /** Set current popup to show */
    setHotelsCurrentPopup: (state, action: PayloadAction<string | null>) => {
      state.currentPopup = action.payload;
    },

    /** Set popup data and show popup */
    showHotelsPopup: (state, action: PayloadAction<{ key: string; data: HotelsPopupData }>) => {
      state.popupMap[action.payload.key] = action.payload.data;
      state.currentPopup = action.payload.key;
    },

    /** Show QnA gallery popup */
    showQnAGalleryPopup: (state, action: PayloadAction<QnAMediaCategory[]>) => {
      state.popupMap[QNA_GALLERY_POPUP_KEY] = { categories: action.payload };
      state.currentPopup = QNA_GALLERY_POPUP_KEY;
    },

    /** Show error popup */
    showHotelsErrorPopup: (state, action: PayloadAction<PopupErrorDetails>) => {
      state.popupMap[API_ERROR_POPUP_KEY] = action.payload;
      state.currentPopup = API_ERROR_POPUP_KEY;
    },

    /** Clear current popup (close popup) */
    clearHotelsPopup: (state) => {
      state.currentPopup = null;
    },

    /** Clear all popups */
    clearAllHotelsPopups: (state) => {
      state.currentPopup = null;
      state.popupMap = {};
    },
  },
  extraReducers: (builder) => {
    const handleIntents = (state: HotelsPopupsState, templateData: unknown) => {
      if (!templateData) return;
      const data = templateData as Record<string, unknown>;
      const intents = extractIntentsFromHotelResponse(data);
      for (const intent of intents) {
        if (intent === HOTEL_INTENTS.HOTEL_MEDIA) {
          const categories = extractMediaFromQnATemplateData(data).filter(
            (category) => category.images && category.images.length > 0,
          );
          if (categories.length > 0) {
            state.popupMap[QNA_GALLERY_POPUP_KEY] = { categories };
            state.currentPopup = QNA_GALLERY_POPUP_KEY;
          }
          return;
        }
      }
    };

    const handleReviewError = (
      state: HotelsPopupsState,
      activeTemplate: string | undefined,
      templateData: unknown,
    ) => {
      if (activeTemplate !== 'hotels-review-error' || !templateData) return;
      const errorData = templateData as {
        isError?: boolean;
        error?: { code: string; message: string };
      };
      if (errorData?.error) {
        state.popupMap[API_ERROR_POPUP_KEY] = {
          errorMessage: errorData.error.message,
        };
        state.currentPopup = API_ERROR_POPUP_KEY;
      }
    };

    builder.addCase(setActiveTemplate, (state, action) => {
      // A real template is taking over the right panel — dismiss any open popup first.
      state.currentPopup = null;
      handleIntents(state, action.payload.data);
    });

    builder.addCase(updateTemplateFromStream, (state, action) => {
      const { activeTemplate, templateData, content } = action.payload;

      // When a real template streams in (hotels-listing, hotels-details, etc.),
      // dismiss any open popup so it doesn't overlay the new template.
      if (activeTemplate && !DATA_ONLY_TEMPLATE_TYPES.has(activeTemplate)) {
        state.currentPopup = null;
      }

      handleReviewError(state, activeTemplate, templateData);

      // For data-only templates like hotels-qna, templateData may also live inside
      // content[].templateData (the card). Use it as a fallback.
      let effectiveTemplateData = templateData;
      if (!effectiveTemplateData && content) {
        const card = content.find((c) => c.type === 'card') as
          | { type: 'card'; templateData?: unknown }
          | undefined;
        effectiveTemplateData = card?.templateData as typeof templateData;
      }

      // For hotels-qna, extract media directly — mirrors nonTemplateCardHandler logic.
      if (activeTemplate === 'hotels-qna' && effectiveTemplateData) {
        const data = effectiveTemplateData as Record<string, unknown>;
        const categories = extractMediaFromQnATemplateData(data).filter(
          (cat) => cat.images && cat.images.length > 0,
        );
        if (categories.length > 0) {
          state.popupMap[QNA_GALLERY_POPUP_KEY] = { categories };
          state.currentPopup = QNA_GALLERY_POPUP_KEY;
          return;
        }
      }

      handleIntents(state, effectiveTemplateData);
    });
  },
});

export const {
  setHotelsCurrentPopup,
  showHotelsPopup,
  showQnAGalleryPopup,
  showHotelsErrorPopup,
  clearHotelsPopup,
  clearAllHotelsPopups,
} = popupsSlice.actions;

export const popupsReducer = popupsSlice.reducer;
